import { useCallback, useRef, useState } from 'react';

import { PRODUCT_NAME } from '../constants/brand';
import { ANALYTICS_EVENTS, track } from '../services/analytics';
import { reportError } from '../services/errorTracking';
import * as billingApi from '../services/billingApi';
import * as planAddonApi from '../services/planAddonApi';

// Web's plan extra-meetings checkout: a ONE-TIME Razorpay order in the
// page - it never opens a subscription. The counterpart of
// useMeetingPassCheckout for N meetings.
//
// start(quantity) resolves to 'paid' (the backend confirmed the payment
// with the provider), 'delayed' (paid, not yet confirmed) or 'dismissed'
// (closed without paying), and rejects when checkout could not open.
// Nothing here decides that credits exist: `addedQuantity` is only ever
// what the backend answered after checking with the provider.
function newKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function usePlanAddonCheckout({ user, onPurchased } = {}) {
  const [stage, setStage] = useState(null);
  const [addedQuantity, setAddedQuantity] = useState(null);
  // One idempotency key per (attempt, quantity): a retry of the same
  // purchase returns the same order, a new quantity is a new purchase.
  const keyRef = useRef(null);
  const busyRef = useRef(false);
  const onPurchasedRef = useRef(onPurchased);
  onPurchasedRef.current = onPurchased;

  const keyFor = (quantity) => {
    if (!keyRef.current || keyRef.current.quantity !== quantity) {
      keyRef.current = { quantity, key: `addon-${newKey()}` };
    }
    return keyRef.current.key;
  };

  const confirm = useCallback(async (handlerResponse) => {
    let status = await planAddonApi.acknowledgeCheckout(handlerResponse);

    for (
      let attempt = 0;
      attempt < billingApi.ACTIVATION_POLL_ATTEMPTS && status.status === 'processing';
      attempt += 1
    ) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((resolve) => {
        setTimeout(resolve, billingApi.ACTIVATION_POLL_INTERVAL_MS);
      });
      try {
        // eslint-disable-next-line no-await-in-loop
        status = await planAddonApi.acknowledgeCheckout(handlerResponse);
      } catch (_) {
        // A failed re-check is not a failed payment - keep trying.
      }
    }
    return status;
  }, []);

  const start = useCallback(
    async (quantity) => {
      if (busyRef.current) return 'dismissed';
      busyRef.current = true;
      setAddedQuantity(null);
      setStage({ stage: 'opening', message: 'Opening checkout…' });

      let checkout;
      let Razorpay;
      try {
        [checkout, Razorpay] = await Promise.all([
          planAddonApi.startCheckout({ quantity, idempotencyKey: keyFor(quantity) }),
          billingApi.loadCheckoutScript(),
        ]);
      } catch (err) {
        busyRef.current = false;
        setStage(null);
        track(ANALYTICS_EVENTS.PURCHASE_FAILED, { kind: 'plan_addon', reason: err?.code || 'checkout_unavailable' });
        // Category and code only - never payment details (services/monitoring.js).
        reportError('billing_checkout', err);
        throw err;
      }

      track(ANALYTICS_EVENTS.CHECKOUT_STARTED, { kind: 'plan_addon', plan_code: 'plan_addon' });
      setStage({ stage: 'awaiting_payment', message: 'Complete the payment in the Razorpay window.' });

      return new Promise((resolve, reject) => {
        const finish = (value) => {
          busyRef.current = false;
          resolve(value);
        };
        const instance = new Razorpay({
          key: checkout.key_id,
          order_id: checkout.provider_order_id,
          name: PRODUCT_NAME,
          description: `${checkout.name} — ${checkout.description}`,
          prefill: { email: user?.email, name: user?.full_name },
          handler: async (response) => {
            setStage({ stage: 'confirming', message: 'Payment received. Confirming…' });
            try {
              const status = await confirm(response);
              if (status.status === 'processing') {
                setStage({
                  stage: 'delayed',
                  message:
                    'Payment received. We are still confirming it - your extra meetings will be added as soon as it is.',
                });
                finish('delayed');
                return;
              }
              if (status.status !== 'paid') {
                // e.g. a payment that arrived after the billing period
                // ended: nothing was added and support will refund it.
                setStage({
                  stage: 'delayed',
                  message:
                    'Your payment could not be applied to extra meetings. It will be refunded - contact support if you do not see it.',
                });
                finish('delayed');
                return;
              }
              keyRef.current = null;
              setStage(null);
              setAddedQuantity(status.quantity ?? quantity);
              track(ANALYTICS_EVENTS.PURCHASE_COMPLETED, { kind: 'plan_addon' });
              onPurchasedRef.current?.(status);
              finish('paid');
            } catch (err) {
              busyRef.current = false;
              setStage(null);
              track(ANALYTICS_EVENTS.PURCHASE_FAILED, { kind: 'plan_addon', reason: 'confirmation_failed' });
              // Category and code only - never payment details (services/monitoring.js).
              reportError('billing_confirmation', err);
              reject(
                new Error(
                  err?.message ||
                    'We could not confirm your payment. If money was taken it will be reconciled automatically.'
                )
              );
            }
          },
          modal: {
            ondismiss: () => {
              setStage(null);
              finish('dismissed');
            },
          },
        });
        instance.open();
      });
    },
    [confirm, user]
  );

  const isBusy = Boolean(stage) && stage.stage !== 'delayed';

  return { stage, addedQuantity, start, isBusy };
}
